import { toLocalizedPath } from "./i18nRoute";

/**
 * Resolve the booking application origin dynamically.
 */
export function getBookingAppOrigin() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BOOKING_APP_ORIGIN || "https://booking.driver2go.fr";
}

/**
 * Build an absolute URL for the booking application.
 * @param {string} path - Relative path (e.g., /bookings/bookingdetails/123)
 * @returns {string} - Full URL
 */
export function buildBookingAppUrl(path) {
  const origin = getBookingAppOrigin();
  return `${origin}${path.startsWith('/') ? path : '/' + path}`;
}

/**
 * Navigation helper that escapes iframes to avoid "site-in-a-site" visual issues.
 * If running inside an iframe, redirects the top-level window.
 * Otherwise, uses the provided SPA navigate function.
 * 
 * @param {string} path - Target relative path
 * @param {function} navigate - React Router navigate function
 * @param {object} options - Navigation options (replace, state, etc.)
 */
export function navigateWithIframeEscape(path, navigate, options = {}) {
  const absoluteUrl = buildBookingAppUrl(path);

  if (typeof window !== "undefined") {
    try {
      // Detect if we are in an iframe
      if (window.self !== window.top) {
        console.log("[IFRAME_ESCAPE] Redirecting parent to:", absoluteUrl);
        if (options.replace) {
          window.top.location.replace(absoluteUrl);
        } else {
          window.top.location.href = absoluteUrl;
        }
        return;
      }
    } catch (error) {
      // In case of cross-origin security errors accessing window.top,
      // fallback to standard location change.
      console.warn("[IFRAME_ESCAPE] Security error or access denied, falling back to window.location.href", error);
      if (options.replace) {
        window.location.replace(absoluteUrl);
      } else {
        window.location.href = absoluteUrl;
      }
      return;
    }
  }

  // Not in an iframe or window undefined: use standard SPA navigation
  if (typeof navigate === "function") {
    navigate(path, options);
  } else {
    // Fallback if navigate is not provided
    window.location.href = absoluteUrl;
  }
}

/**
 * Specialized version that handles i18n routing AND iframe escape.
 * Use this for all post-booking redirections.
 * 
 * @param {function} navigate - React Router navigate function
 * @param {string} path - Target relative path (WITHOUT locale prefix)
 * @param {string} langOrPathname - Current language or pathname for inference
 * @param {object} options - Navigation options
 */
export function navigateLocalizedWithIframeEscape(navigate, path, langOrPathname, options = {}) {
    const localizedPath = toLocalizedPath(path, langOrPathname);
    navigateWithIframeEscape(localizedPath, navigate, options);
}
